'use client';

import useSWRMutation from 'swr/mutation';
import { SWR_CACHE_KEYS } from '@helpers/constants';
import type { FetchRegisterUserBody } from '@helpers/api/fetch-register-user/fetch-register-user';
import { fetchRegisterUser } from '@helpers/api/fetch-register-user/fetch-register-user';
import type { FetchLoginUserBody } from '@helpers/api/fetch-login-user/fetch-login-user';
import { fetchLoginUser } from '@helpers/api/fetch-login-user/fetch-login-user';
import useSWR, { useSWRConfig } from 'swr';
import type { UserModel, UserRole } from '@helpers/types';

export const useUser = () => {
  const { mutate } = useSWRConfig();

  const userKey = SWR_CACHE_KEYS.USER;

  const {
    data: userData,
    isLoading: isUserDataLoading,
    error: userError,
    mutate: mutateUserLogin
  } = useSWR(userKey, async () => {
    console.log('Fetching user data for user:');
    const response = await fetch('/api/v1/user', {
      credentials: 'include',
      method: 'GET'
    });

    if (!response.ok) {
      console.error(
        'Error fetching user data and setting user data to anonymous',
        response
      );
    }

    const userFullData = (await response.json()) as {
      success: boolean;
      data: UserModel;
    };

    return {
      isLoggedIn: true,
      avatarUrl: userFullData.data.avatarUrl ?? null,
      userName: userFullData.data.userName,
      socialUrl: userFullData.data.socialUrl ?? null,
      role: userFullData.data.role as UserRole
    };
  });

  const {
    trigger: mutateRegisterUser,
    isMutating: isMutatingRegisterUser,
    data: registeredUserData,
    error: registerUserError
  } = useSWRMutation(
    userKey,
    (_: string, { arg }: { arg: FetchRegisterUserBody }) =>
      fetchRegisterUser(arg),
    { throwOnError: false }
  );

  const {
    trigger: mutateLoginUser,
    isMutating: isMutatingLoginUser,
    error: loginUserError
  } = useSWRMutation(
    userKey,
    async (url, { arg }: { arg: FetchLoginUserBody }) => {
      const data = await fetchLoginUser(arg);
      await mutate(
        userKey,
        {
          isLoggedIn: true,
          avatarUrl: data.data.avatarUrl || null,
          socialUrl: data.data.socialUrl || null,
          userName: data.data.userName,
          role: data.data.role
        },
        {
          revalidate: false
        }
      );
      return data;
    }
  );

  const {
    trigger: mutateLogoutUser,
    isMutating: isMutatingLogoutUser,
    error: logoutUserError
  } = useSWRMutation('/api/v1/user/logout', () =>
    fetch('/api/v1/user/logout', { method: 'DELETE', credentials: 'include' })
  );

  return {
    userData,
    isUserDataLoading,
    userError,
    mutateRegisterUser,
    registeredUserData,
    isMutatingRegisterUser,
    registerUserError,
    mutateUserLogin,
    mutateLoginUser,
    isMutatingLoginUser,
    loginUserError,
    mutateLogoutUser,
    isMutatingLogoutUser,
    logoutUserError
  };
};
